import React, { useEffect, useMemo, useState } from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native'
import { Image } from 'expo-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import ChevronChico from '../../../../components/ChevronChico';
import { Icon } from '../../../../components/Icon';
import { Illustration } from '../../../../components/Illustration';
import { useDispatch } from 'react-redux';
import { actions } from '../../../../store/Reducers/auth';
import { useQuery } from '@tanstack/react-query';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import api from '../../../../utils/api';


interface SettingsCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onPress: () => void;
  selected?: boolean;
}

function SettingsCard({ icon, title, subtitle, onPress, selected }: SettingsCardProps) {
  return (
    <TouchableOpacity
      style={tw.style(`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3 row items-center`, selected && `bg-primary-50 border-primary`)}
      onPress={onPress}
    >
      <View style={tw`w-11 h-11 items-center justify-center mr-3`}>{icon}</View>

      <View style={tw`flex-1`}>
        <Text style={tw`text-secondary font-600 text-base`}>{title}</Text>
        {subtitle && <Text style={tw`text-gray-500 text-sm`}>{subtitle}</Text>}
      </View>

      <ChevronChico />
    </TouchableOpacity>
  )
}


const SETTINGS_ROUTES = [
  'Admin.Settings.Maintenance',
  'Admin.Settings.Users',
  'Admin.Settings.QRAccess',
  'Admin.Settings.Notifications',
  'Admin.Settings.Mora',
  'Admin.Settings.Vigilancia',
  'Admin.Settings.Expenses',
  'Admin.Settings.Reports',
  'Admin.Settings.ProviderAuth',
]

/**
 * En web (master-detail) la pantalla vive en el navegador interno y cada opción se abre como
 * detail en el stack de afuera. Devuelve la opción cuyo detail está abierto; si encima hay una
 * sub-pantalla (ej. QRCategory sobre QRAccess), cuenta la opción de la que salió.
 */
function useOpenSettingsRoute(navigation: any, enabled: boolean): string | undefined {
  const parent = enabled ? navigation.getParent() : undefined
  const pick = (state: any) => [...(state?.routes ?? [])].reverse().find(r => SETTINGS_ROUTES.includes(r.name))?.name
  const [route, setRoute] = useState<string | undefined>(() => pick(parent?.getState()))

  useEffect(() => {
    if (!parent) return
    setRoute(pick(parent.getState()))
    return parent.addListener('state', (e: any) => setRoute(pick(e.data.state)))
  }, [parent])

  return route
}


export default function AdminSettingsScreen({ navigation, route }) {
  const { communityId, community, can } = useActiveAdminCommunity()
  const canManageSettings = can('settings.manage')
  const canManageStaff = can('staff.manage')
  const dispatch = useDispatch()
  const insets = useSafeAreaInsets()
  const isWeb = Platform.OS === 'web'
  const openRoute = useOpenSettingsRoute(navigation, isWeb)

  const cardProps = (route: string) => ({
    selected: openRoute === route,
    onPress: () => navigation.navigate(route),
  })

  // Query para obtener el conteo del equipo
  const { data: staff } = useQuery({
    queryKey: ['community.staff', communityId],
    queryFn: () => api.get(`/communities/${communityId}/staff`).then(response => response.data),
    enabled: canManageStaff,
  })

  // Query para obtener el fee type principal
  const { data: feeTypes } = useQuery({
    queryKey: ['community.fees.levels', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/types`).then(response => response.data),
    enabled: canManageSettings,
  })

  const mainFeeType = useMemo(() => {
    return feeTypes?.find(ft => ft.type === 'main' && !ft.is_variant)
  }, [feeTypes])

  const variantsCount = useMemo(() => {
    return feeTypes?.filter(ft => ft.type === 'main' && ft.is_variant).length ?? 0
  }, [feeTypes])

  const staffCount = staff?.length ?? 0

  return (
    <ScrollView style={tw`flex-1 bg-background`} contentContainerStyle={tw.style(`px-4 pb-10`, { paddingTop: isWeb ? 16 : insets.top + 16 })}>
      {/* Header con logo de comunidad (en web ya se muestra arriba, se omite) */}
      {!isWeb && (
        <View style={tw`mb-6`}>
          <Text style={tw`text-2xl font-600 text-secondary mb-4`}>Configuración</Text>

          <View style={tw`row items-center`}>
            {community?.image_path ? (
              <Image
                source={{ uri: community.image_path }}
                style={tw`h-12 w-12 rounded-xl mr-3`}
                contentFit='contain'
              />
            ) : (
              <View style={tw`h-12 w-12 rounded-xl bg-secondary items-center justify-center mr-3`}>
                <Text style={tw`text-white font-600 text-lg`}>{community?.name?.charAt(0) ?? 'C'}</Text>
              </View>
            )}
            <Text style={tw`text-gray-700 font-500 text-lg`}>{community?.name}</Text>
          </View>
        </View>
      )}

      {/* Opciones de configuración */}
      {canManageSettings && <SettingsCard
        icon={<Icon name="finanzas" size={40} />}
        title="Cuota principal (mantenimiento)"
        subtitle={mainFeeType ? `$${mainFeeType.amount}${variantsCount > 0 ? `  ${variantsCount} variante${variantsCount > 1 ? 's' : ''}` : ''}` : undefined}
        {...cardProps('Admin.Settings.Maintenance')}
      />}

      {canManageStaff && <SettingsCard
        icon={<Icon name="configAdmins" size={40} />}
        title="Equipo"
        subtitle={`${staffCount} persona${staffCount !== 1 ? 's' : ''}`}
        {...cardProps('Admin.Settings.Users')}
      />}

      {canManageSettings && <>
      <SettingsCard
        icon={<Icon name="qr" variant="v2" size={40} />}
        title="Acceso QR"
        {...cardProps('Admin.Settings.QRAccess')}
      />

      <SettingsCard
        icon={<Icon name="configNotificaciones" size={40} />}
        title="Notificaciones"
        {...cardProps('Admin.Settings.Notifications')}
      />

      <SettingsCard
        icon={<Icon name="configMora" size={40} />}
        title="Morosidad"
        {...cardProps('Admin.Settings.Mora')}
      />

      <SettingsCard
        icon={<Icon name="configAppV" size={40} />}
        title="App de vigilancia"
        {...cardProps('Admin.Settings.Vigilancia')}
      />

      <SettingsCard
        icon={<Icon name="historialPagos" size={40} />}
        title="Gastos"
        {...cardProps('Admin.Settings.Expenses')}
      />

      <SettingsCard
        icon={<Illustration name="reportes" width={40} height={40} />}
        title="Reportes y solicitudes"
        {...cardProps('Admin.Settings.Reports')}
      />

      <SettingsCard
        icon={<Illustration name="autorizacion" width={40} height={40} />}
        title="Autorización a proveedores"
        {...cardProps('Admin.Settings.ProviderAuth')}
      />
      </>}

      {/* Cerrar sesión */}
      <TouchableOpacity
        style={tw`mt-8 py-3`}
        onPress={() => dispatch(actions.logout())}
      >
        <Text style={tw`text-center text-gray-500`}>Cerrar sesión</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}
